interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}

/**
 * Simple prev/next pagination. `page` is 1-based; callers pass the total
 * pages and receive a new 1-based page index. Hidden entirely when there is
 * only one page (or zero) to avoid visual noise on tiny datasets.
 */
export default function Pagination({
  page,
  totalPages,
  onPageChange,
  disabled,
}: PaginationProps) {
  const hasPrev = page > 1 && !disabled;
  const hasNext = page < totalPages && !disabled;

  if (totalPages <= 1) {
    return null;
  }

  return (
    <div
      className="flex items-center justify-between mt-4"
      data-testid="pagination"
    >
      <button
        type="button"
        onClick={() => onPageChange(page - 1)}
        disabled={!hasPrev}
        data-testid="pagination-prev"
        className="text-sm border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Previous
      </button>
      <div
        className="text-sm text-slate-600"
        data-testid="pagination-status"
      >
        Page <span className="font-medium text-slate-900">{page}</span> of{' '}
        <span className="font-medium text-slate-900">{totalPages}</span>
      </div>
      <button
        type="button"
        onClick={() => onPageChange(page + 1)}
        disabled={!hasNext}
        data-testid="pagination-next"
        className="text-sm border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Next
      </button>
    </div>
  );
}
